<template>
  <div>
    <SiteHeader :page-name="'Subdivisions'" />
    <div class="mt-4 w-full flex flex-col m-auto" style="height: 90vh">
      <Toolbar pt:root:class="mx-4 py-2">
        <template #start>
          <div class="flex flex-row gap-2">
            <SearchBar v-model:search-text="searchText" show-refresh @input="onFilterTextBoxChanged" @refresh="populateSubdivisions"></SearchBar>
            <ToolbarButtons label="Subdivision" show-add @add="add = true"></ToolbarButtons>
            <ReportsListButton :context="'ALL_SUBDIVISIONS'"></ReportsListButton>
          </div>
        </template>
        <template #end>
          <SplitButton
            :label="selectedSubdivision ? selectedSubdivision.subdivisionName : 'Selected Subdivision'"
            severity="secondary"
            menuButtonIcon="pi pi-cog text-xl"
            :model="menuItems"
            :disabled="!selectedSubdivision"
            @click="displaySubdivision(selectedSubdivision)"
          />
        </template>
      </Toolbar>
      <div style="width: 100%; flex: 1 1 auto">
        <ag-grid-vue
          theme="legacy"
          :rowData="subdivisions"
          :columnDefs="colDefs"
          :defaultColDef="defaultColDefs"
          :autoSizeStrategy="{ type: 'fitCellContents' }"
          :suppressCellFocus="true"
          class="mx-4 py-3 h-full"
          :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
          rowSelection="single"
          @row-selected="selectSubdivision"
          @row-double-clicked="displaySubdivision(selectedSubdivision)"
          @row-data-updated="resize"
          @grid-size-changed="resize"
          @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
        >
        </ag-grid-vue>
      </div>
      <TSDialog v-if="add" label="Add Subdivision" v-model:visible="add" modal size="w-[900px]!" v-on:after-hide="add = false" :icon="'plus'">
        <template #content>
          <AddSubdivision @subdivision-added="subdivisionAdded" />
        </template>
      </TSDialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import SiteHeader from "@/components/SiteHeader.vue";
import { confirmPopup, currencyFormatter, dateFormatter, handleApiResponse, parseData, percentFormatter } from "@/services/Utility";
import { usePropsStore } from "@/stores/props";
import { SubdivisionListSchema, type Subdivision } from "@/types/Subdivision";
import axios from "axios";
import { computed, onBeforeMount, ref } from "vue";
import type { ApiError } from "@/types/Response";
import AddSubdivision from "@/components/subdivisions/AddSubdivision.vue";
import { error, errorApi, info, success } from "@/services/ToastService";
import { useRouter } from "vue-router";
import SplitButton from "primevue/splitbutton";
import { useConfirm } from "primevue/useconfirm";
import type { GridApi, GridReadyEvent, GridSizeChangedEvent, RowDataUpdatedEvent, RowSelectedEvent, ValueGetterParams } from "ag-grid-community";
import type { MenuItem } from "primevue/menuitem";

const add = ref<boolean>(false);

const gridApi = ref<GridApi>();
const searchText = ref("");
const confirm = useConfirm();

const propsStore = usePropsStore();
const subdivisions = ref<Subdivision[]>([]);
const selectedSubdivision = ref<Subdivision>();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const router = useRouter();

const menuItems: MenuItem[] = [
  {
    label: "Delete",
    icon: "pi pi-trash",
    command: () => askDeleteSubdivision(),
  },
];

onBeforeMount(async (): Promise<void> => {
  await populateSubdivisions();
});

const populateSubdivisions = async (): Promise<void> => {
  await axios
    .get("/subdivision/list")
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => parseData(SubdivisionListSchema, data, subdivisions),
        (errors: ApiError[]) => {
          subdivisions.value = [];
          error("An error occurred", "Could not retrieve subdivisions");
        }
      )
    )
    .catch((e) => errorApi(e));
};

const subdivisionAdded = (subdivision: Subdivision): void => {
  add.value = false;
  displaySubdivision(subdivision);
};

const displaySubdivision = (subdivision: Subdivision | undefined): void => {
  if (!subdivision) return;

  router.push({
    name: "subdivision-dashboard",
    params: {
      id: subdivision.subdivisionId,
    },
  });
};

const askDeleteSubdivision = (): void => {
  if (!selectedSubdivision.value) {
    info("No Entity Selected", "Select an entity to manage");
    return;
  }

  confirmPopup(
    confirm,
    "Delete Subdivision?",
    "Are you sure you want to delete the selected subdivision?  By deleting this subdivision, the projects that are associated with it will have their subdivision set to none?",
    () => deleteSubdivision(),
    () => info("Action cancelled", "Subdivision not deleted")
  );
};

const deleteSubdivision = (): void => {
  if (!selectedSubdivision.value) return;

  axios
    .delete(`/subdivision/delete/${selectedSubdivision.value.subdivisionId}`)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          populateSubdivisions();
          success("Success", "Subdivision deleted");
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

const selectSubdivision = (event: RowSelectedEvent): void => {
  let node = event.node;

  if (node.isSelected()) {
    selectedSubdivision.value = node.data;
  }
};

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const onFilterTextBoxChanged = (): void => {
  gridApi.value?.setGridOption("quickFilterText", searchText.value);
};

const defaultColDefs = {
  filter: true,
  floatingFilter: false,
  cellDataType: "text",
};

const colDefs = ref([
  {
    headerName: "User Group",
    width: 100,
    valueGetter: (params: ValueGetterParams) => params.data.userGroup.name,
    pinned: true,
  },
  {
    field: "subdivisionName",
    headerName: "Name",
    width: 100,
    pinned: true,
  },
  {
    headerName: "Loan #",
    width: 75,
    valueGetter: (params: ValueGetterParams) => "",
  },
  {
    field: "startDate",
    headerName: "Start Date",
    width: 100,
    valueFormatter: dateFormatter,
  },
  {
    field: "maturityDate",
    headerName: "Maturity Date",
    width: 100,
    valueFormatter: dateFormatter,
  },
  {
    cellDataType: "number",
    field: "totalUnits",
    headerName: "Units",
    width: 75,
    cellClass: "text-center",
    headerClass: "grid-align-center",
  },
  {
    headerName: "S-A-P",
    width: 75,
    cellClass: "text-center",
    headerClass: "grid-align-center",
    valueGetter: (params: ValueGetterParams) => params.data.totalSold + "-" + params.data.totalActive + "-" + params.data.totalPending,
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "creditLine",
    headerName: "Credit Line",
    headerClass: "grid-align-center",
    width: 100,
    valueFormatter: currencyFormatter,
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalEquity",
    headerName: "Equity",
    headerClass: "grid-align-center",
    width: 100,
    valueFormatter: currencyFormatter,
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalBudget",
    headerName: "Total Budget",
    headerClass: "grid-align-center",
    width: 100,
    valueFormatter: currencyFormatter,
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalCost",
    headerName: "Total Cost",
    headerClass: "grid-align-center",
    width: 100,
    valueFormatter: currencyFormatter,
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalDrawnToDate",
    headerName: "Drawn To Date",
    headerClass: "grid-align-center",
    width: 100,
    valueFormatter: currencyFormatter,
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalFundsAvailable",
    headerName: "Funds Available",
    headerClass: "grid-align-center",
    width: 100,
    valueFormatter: currencyFormatter,
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalPercentComplete",
    headerName: "% Drawn",
    headerClass: "grid-align-center",
    width: 50,
    valueFormatter: percentFormatter,
    valueGetter: (params: ValueGetterParams) =>
      params.data.totalBudget !== 0 ? Math.round(params.data.totalDrawnToDate / params.data.totalBudget) : 0,
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    headerName: "% Inspected",
    headerClass: "grid-align-center",
    width: 50,
    valueFormatter: percentFormatter,
    valueGetter: (params: ValueGetterParams) =>
      params.data.totalUnits > 0 ? Math.round(params.data.totalPercentComplete / params.data.totalUnits) : 0,
  },
]);
</script>
